import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ShowsSection.css";

// ---- Date parsing: extract the latest show date from the title text ----
const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Pulls all "<MonthAbbr> <Day>" occurrences from a title (e.g. "Mar 5",
 * "Sept 18 to Sept 20") and returns the LATEST as a Date. Multi-day
 * events stay visible until the last date passes.
 * If the month/day would be >60 days in the past, we assume it's next
 * year (so January events in December don't get hidden).
 */
function parseEventDate(title) {
  if (!title) return null;

  const re = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})\b/gi;
  const matches = [...title.matchAll(re)];
  if (matches.length === 0) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let latest = null;
  for (const m of matches) {
    const month = MONTHS[m[1].toLowerCase()];
    const day = parseInt(m[2], 10);
    if (month === undefined || Number.isNaN(day)) continue;

    let candidate = new Date(now.getFullYear(), month, day);
    const daysAgo = (today - candidate) / 86400000;
    if (daysAgo > 60) {
      candidate = new Date(now.getFullYear() + 1, month, day);
    }
    if (!latest || candidate > latest) latest = candidate;
  }
  return latest;
}

function isUpcoming(event) {
  const eventDate = parseEventDate(event.title);
  // If we can't parse a date, keep the event (better to show than to hide).
  if (!eventDate) return true;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return eventDate >= today;
}

const ShowsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadEvents = useCallback(async (retries = 1) => {
    try {
      setLoading(true);
      setError(false);

      const response = await fetch("/shows.json", { cache: "no-store" });
      if (!response.ok) throw new Error("Failed to load shows.json");

      const data = await response.json();
      setEvents(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => loadEvents(retries - 1), 2000);
      } else {
        setError(true);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Filter out past events and sort upcoming ones chronologically.
  const upcomingEvents = useMemo(() => {
    return events
      .filter(isUpcoming)
      .sort((a, b) => {
        const da = parseEventDate(a.title);
        const db = parseEventDate(b.title);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      });
  }, [events]);

  const handleRetry = () => loadEvents();

  const parseEvent = (rawTitle) => {
    const title = (rawTitle || "").trim();
    const dashIndex = title.indexOf("-");
    if (dashIndex === -1) return { comedian: title, location: "" };
    return {
      comedian: title.slice(0, dashIndex).trim(),
      location: title.slice(dashIndex + 1).trim(),
    };
  };

  return (
    <section className="shows-section">
      <header className="shows-header">
        <span className="shows-eyebrow">
          Tour Schedule
        </span>
        <h1 className="shows-title">Show Dates</h1>
      </header>

      <div className="event-list">
        {loading ? (
          <div className="shows-state">
            <span className="loading-spinner" aria-label="Loading shows" />
          </div>
        ) : error ? (
          <div className="shows-state">
            <p className="shows-state-message">Unable to load upcoming shows.</p>
            <button type="button" className="shows-refresh" onClick={handleRetry}>
              <span className="shows-refresh-label">Try Again</span>
              <span className="shows-refresh-icon" aria-hidden="true">↻</span>
            </button>
          </div>
        ) : upcomingEvents.length === 0 ? (
          <div className="shows-state">
            <p className="shows-state-message">No upcoming shows at the moment.</p>
            <p className="shows-state-sub">Check back soon.</p>
          </div>
        ) : (
          upcomingEvents.map((event, index) => {
            const { comedian, location } = parseEvent(event.title);
            return (
              <article className="event-item" key={index}>
                <span className="event-index" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="event-info">
                  <h3 className="event-title">{comedian}</h3>
                  {location && <p className="event-location">{location}</p>}
                </div>

                <a
                  className="event-cta"
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="event-cta-label">Get Tickets</span>
                  <span className="event-cta-arrow" aria-hidden="true">→</span>
                </a>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
};

export default ShowsSection;
